import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Upload, Share2, X, Download, Maximize, ChevronDown, Check, Clock, Lock, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import Button from '../../components/ui/Button';
import UploadPhotoGuest from '../../components/modals/UploadPhotoGuest';
import { toast } from 'react-toastify';
import { useCheckEventPasswordMutation } from '../../store/services/api';
import type { Event } from '../../types/event';
import type { Photo } from '../../types/photos';

const PHOTOS_PER_PAGE = 15;

// Helper function to manage event passwords in localStorage
const eventPasswordManager = {
  getPassword: (eventUrl: string) => {
    try {
      const passwords = JSON.parse(localStorage.getItem('eventPasswords') || '{}');
      return passwords[eventUrl] || '';
    } catch {
      return '';
    }
  },
  
  setPassword: (eventUrl: string, password: string) => {
    try {
      const passwords = JSON.parse(localStorage.getItem('eventPasswords') || '{}');
      passwords[eventUrl] = password;
      localStorage.setItem('eventPasswords', JSON.stringify(passwords));
      console.log('Password saved for event:', eventUrl);
    } catch (error) {
      console.error('Error saving password:', error);
    }
  },
  
  removePassword: (eventUrl: string) => {
    try {
      const passwords = JSON.parse(localStorage.getItem('eventPasswords') || '{}');
      delete passwords[eventUrl];
      localStorage.setItem('eventPasswords', JSON.stringify(passwords));
    } catch (error) {
      console.error('Error removing password:', error);
    }
  }
};

const PublicEventPage = () => {
  const { url } = useParams<{ url: string }>();
  
  const [checkPassword, { 
    isLoading: isCheckingPassword 
  }] = useCheckEventPasswordMutation();

  // State tanımlamaları
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [eventTitle, setEventTitle] = useState('Password Protected Event');
  const [isLoading, setIsLoading] = useState(true);
  const [eventData, setEventData] = useState<{ event: Event | null; photos: Photo[] }>({
    event: null,
    photos: []
  });
  
  // UI state tanımlamaları
  const [expandedPhoto, setExpandedPhoto] = useState<Photo | null>(null);
  const [isExpandedPhotoLoading, setIsExpandedPhotoLoading] = useState(false);
  const [visiblePhotos, setVisiblePhotos] = useState<number>(PHOTOS_PER_PAGE);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [downloadingPhoto, setDownloadingPhoto] = useState<number | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  // Debug logging
  useEffect(() => {
    console.log('Event data:', eventData);
    console.log('Is password protected:', isPasswordProtected);
    console.log('Is password verified:', isPasswordVerified);
  }, [eventData, isPasswordProtected, isPasswordVerified]);
  
  // Sayfa yüklendiğinde etkinliği kontrol et
  useEffect(() => {
    const checkEvent = async () => {
      if (!url) return;

      try {
        // Önce kaydedilmiş şifreyi kontrol et
        const savedPassword = eventPasswordManager.getPassword(url);
        
        if (savedPassword) {
          // Kaydedilmiş şifre varsa, doğrula
          const result = await checkPassword({ url, password: savedPassword }).unwrap();
          
          if (result.success) {
            setIsPasswordVerified(true);
            setIsPasswordProtected(false);
            setEventData({
              event: result.data.event || null,
              photos: result.data.photos || []
            });
          } else {
            eventPasswordManager.removePassword(url);
            // Şifre geçersiz, boş şifre ile tekrar dene
            await checkEventWithoutPassword();
          }
        } else {
          // Kaydedilmiş şifre yoksa, boş şifre ile dene
          await checkEventWithoutPassword();
        }
      } catch (error) {
        console.error('Error checking event:', error);
        setIsPasswordProtected(false);
        setEventData({ event: null, photos: [] });
      } finally {
        setIsLoading(false);
      }
    };

    const checkEventWithoutPassword = async () => {
      try {
        const result = await checkPassword({ url: url || '', password: '' }).unwrap();
        
        if (result.success) {
          // Etkinlik şifresiz
          setIsPasswordVerified(true);
          setIsPasswordProtected(false);
          setEventData({
            event: result.data.event || null,
            photos: result.data.photos || []
          });
        } else {
          // Etkinlik şifreli
          setIsPasswordProtected(true);
          setIsPasswordVerified(false);
          if (result.data?.eventTitle) {
            setEventTitle(result.data.eventTitle);
          }
        }
      } catch (error: any) {
        if (error.status === 401) {
          // Şifre gerekiyor
          setIsPasswordProtected(true);
          setIsPasswordVerified(false);
          if (error.data?.eventTitle) {
            setEventTitle(error.data.eventTitle);
          }
        } else {
          // Diğer hatalar - etkinlik bulunamadı vs.
          setIsPasswordProtected(false);
          setEventData({ event: null, photos: [] });
        }
      }
    };

    checkEvent();
  }, [url, checkPassword]);
  
  // Şifre kontrol işlemi
  const handlePasswordCheck = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!url || !password.trim()) {
      setPasswordError('Please enter the password');
      return;
    }
    
    setPasswordError('');
    
    try {
      const result = await checkPassword({ url, password }).unwrap();
      
      if (result.success) {
        // Şifre doğru
        eventPasswordManager.setPassword(url, password);
        setIsPasswordVerified(true);
        setIsPasswordProtected(false);
        setEventData({
          event: result.data.event || null,
          photos: result.data.photos || []
        });
      } else {
        // Şifre yanlış
        setPasswordError(result.error || 'Incorrect password. Please try again.');
      }
    } catch (error: any) {
      console.error('Password check error:', error);
      setPasswordError(error.data?.error || 'Could not verify password. Please try again.');
    } finally {
      setPassword('');
    }
  };
  
  // Helper functions for UI
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };
  
  const getThumbnailUrl = (photo: Photo) => {
    return photo.thumbnail_url || photo.public_url || '';
  };
  
  const handleShareClick = async () => {
    try {
      const shareUrl = `https://ourphotos.co/e/${url}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };
  
  const handleLoadMore = () => {
    if (loadingMore) return;
    
    setLoadingMore(true);
    
    setTimeout(() => {
      setVisiblePhotos(prevVisible => {
        const newVisible = prevVisible + PHOTOS_PER_PAGE;
        return Math.min(newVisible, eventData.photos.length);
      });
      setLoadingMore(false);
    }, 500);
  };
  
  const handleDownloadPhoto = (photo: Photo, e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadingPhoto(photo.id);
    
    try {
      fetch(photo.public_url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.setAttribute('download', photo.file_name || `photo-${photo.id}.jpg`);
          anchor.setAttribute('rel', 'noopener noreferrer');
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          
          setTimeout(() => {
            document.body.removeChild(anchor);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        })
        .catch(() => {
          toast.error('Error downloading image');
        });
    } catch (error) {
      toast.error('Download failed');
    }
    
    setTimeout(() => {
      setDownloadingPhoto(null);
    }, 1000);
  };
  
  const handleExpandPhoto = (photo: Photo) => {
    setExpandedPhoto(photo);
    setIsExpandedPhotoLoading(true);
  };
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    const photoId = target.dataset.photoId;
    const photo = eventData.photos.find(p => p.id.toString() === photoId);
    
    if (photo && photo.public_url) {
      target.src = photo.public_url;
    } else {
      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f0f0f0"/%3E%3Cpath d="M40,30 L60,30 L60,70 L40,70 Z" fill="%23ccc"/%3E%3C/svg%3E';
    }
  };
  
  // UI preparation values
  const displayedPhotos = eventData.photos.slice(0, visiblePhotos);
  const hasMorePhotos = eventData.photos.length > visiblePhotos;
  const canViewPhotos = eventData.event?.allow_photo_views === true;
  const canUploadPhotos = eventData.event?.allow_guest_uploads === true;
  
  // Şifre form görünümü
  if (isPasswordProtected && !isPasswordVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg overflow-hidden max-w-md w-full shadow-md">
          <div className="p-6">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-1">{eventTitle}</h2>
            <p className="text-sm text-gray-500 text-center mb-6">This event is password protected</p>
            
            <form onSubmit={handlePasswordCheck}>
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md ${passwordError ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter event password"
                  disabled={isCheckingPassword}
                  autoFocus
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full h-10"
                disabled={isCheckingPassword}
              >
                {isCheckingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Access Event'
                )}
              </Button>
            </form>
            
            <p className="mt-4 text-xs text-center text-gray-500">
              If you don't know the password, please contact the event creator
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF4444] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }
  
  // Error state - no event found
  if (!eventData.event && !isPasswordProtected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200 max-w-md w-full text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-medium mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-4">The event you're looking for doesn't exist or is no longer available.</p>
          <a href="/" className="inline-block text-[#FF4444] hover:underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // İçerik görünümü
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Event Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto py-4 px-4">
          <h1 className="text-xl font-semibold text-gray-900 capitalize mb-1">{eventData.event?.title}</h1>
          <p className="text-sm text-gray-500 mb-2">Created {formatDate(eventData.event?.created_at || '')}</p>
          
          <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-[#FF4444]" />
              <span>{eventData.event?.location}</span>
            </div>
            {eventData.event?.duration && (
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-[#FF4444]" />
                <span>Duration: {eventData.event.duration === 'unlimited' 
                  ? 'Unlimited'
                  : eventData.event.duration.includes('days') 
                    ? eventData.event.duration.replace('days', ' days')
                    : eventData.event.duration}</span>
              </div>
            )}
            {eventData.event?.expires_at && eventData.event.expires_at !== "0001-01-01T00:00:00Z" && (
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-[#FF4444]" />
                <span>Expires: {formatDate(eventData.event.expires_at)}</span>
              </div>
            )}
            {!canViewPhotos && (
              <div className="flex items-center gap-1.5 text-sm text-amber-600">
                <Lock className="w-4 h-4" />
                <span>Private Photo Viewing</span>
              </div>
            )}
            {eventData.event?.has_password && (
              <div className="flex items-center gap-1.5 text-sm text-amber-600">
                <Lock className="w-4 h-4" />
                <span>Password Protected</span>
              </div>
            )}
          </div>

          {eventData.event?.description && (
            <p className="text-sm text-gray-600 mb-4 max-w-2xl">{eventData.event.description}</p>
          )}

          <div className="flex flex-wrap gap-2">
            {canUploadPhotos && (
              <Button 
                onClick={() => setIsUploadModalOpen(true)}
                className="h-8 text-xs"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Upload Photos
              </Button>
            )}
            <Button 
              variant={copied ? "primary" : "outline"}
              className={`h-8 text-xs transition-colors ${copied ? 'bg-green-500 border-green-500 hover:bg-green-600' : ''}`}
              onClick={handleShareClick}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Copied!
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5 mr-1.5" />
                  Share
                </>
              )}
            </Button>
          </div>

          {/* Privacy and upload notifications */}
          {!canViewPhotos && (
            <div className="mt-3 p-2.5 bg-amber-50 rounded-md text-xs text-amber-800 border border-amber-200 flex items-start gap-1.5">
              <EyeOff className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Private Photo Viewing</p>
                <p>Photos are hidden from public view. Only the event owner can see the photos.</p>
              </div>
            </div>
          )}
          {!canUploadPhotos && (
            <div className="mt-2 p-2.5 bg-gray-50 rounded-md text-xs text-gray-700 border border-gray-200 flex items-start gap-1.5">
              <Lock className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <p>
                <span className="font-medium">Guest Uploads Disabled:</span> Only the event creator can upload photos to this collection.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Photo Gallery (only shown if allow_photo_views is true) */}
      {canViewPhotos ? (
        <div className="container mx-auto py-4 px-4">
          {/* Simple Stats Bar */}
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{eventData.photos.length}</span> photos shared
              <span className="text-gray-400 ml-2">
                (Showing {Math.min(visiblePhotos, eventData.photos.length)} of {eventData.photos.length})
              </span>
            </div>
          </div>

          {/* Photos Content */}
          {eventData.photos && eventData.photos.length > 0 ? (
            /* Photos Grid */
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {displayedPhotos.map((photo) => (
                  <div 
                    key={photo.id} 
                    className="relative group overflow-hidden rounded-lg shadow-sm cursor-pointer"
                    onClick={() => handleExpandPhoto(photo)}
                  >
                    <img 
                      src={getThumbnailUrl(photo)} 
                      data-photo-id={photo.id}
                      alt={photo.file_name || "Event photo"} 
                      className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={handleImageError}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-2 left-2 text-white text-xs">
                        <div className="font-medium">{photo.is_guest ? 'Uploaded by Guest' : 'Uploaded by Host'}</div>
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button 
                          className="w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white transition-all hover:bg-black/70"
                          onClick={(e) => handleDownloadPhoto(photo, e)}
                        >
                          {downloadingPhoto === photo.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button 
                          className="w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white transition-all hover:bg-black/70"
                        >
                          <Maximize className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {photo.is_guest && (
                      <div className="absolute top-2 left-2 bg-cyan-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                        Guest
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMorePhotos && (
                <div className="flex justify-center pt-2 pb-6">
                  <Button
                    onClick={handleLoadMore}
                    variant="outline"
                    className="h-9 text-xs px-4 py-2 flex items-center gap-2"
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Load More Photos
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Empty state */
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No photos yet</h3>
              <p className="text-gray-600 mb-4">Be the first to upload photos to this event</p>
              {canUploadPhotos && (
                <Button 
                  onClick={() => setIsUploadModalOpen(true)}
                  className="h-8 text-xs"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload Photos
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Private Photo View message */
        <div className="container mx-auto py-8 px-4">
          <div className="bg-white p-8 rounded-lg border border-gray-100 text-center">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <EyeOff className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-medium mb-2">Private Photo Collection</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              The photos in this event are private. Only the event owner can view them.
            </p>
            
            {/* Still allow uploads if enabled */}
            {canUploadPhotos && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-3">You can still upload photos to this event</p>
                <Button 
                  onClick={() => setIsUploadModalOpen(true)}
                  className="h-8 text-xs"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload Photos
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-auto">
        <div className="container mx-auto py-3 px-4 text-center text-xs text-gray-500">
          <p>Powered by <a href="/" className="text-[#FF4444] hover:underline">OurPhotos</a></p>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && eventData.event && (
        <UploadPhotoGuest
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          title={eventData.event.title}
          eventUrl={eventData.event.url}
          onPhotoUploadComplete={async () => {
            // Yeni fotoğraf yüklendiğinde şifreyi tekrar kontrol et ve verileri güncelle
            try {
              const savedPassword = eventPasswordManager.getPassword(url || '');
              const result = await checkPassword({ 
                url: url || '', 
                password: savedPassword || '' // Şifre yoksa boş string gönder
              }).unwrap();
              
              if (result.success) {
                setEventData({
                  event: result.data.event || null,
                  photos: result.data.photos || []
                });
              }
            } catch (error) {
              console.error('Error refreshing event data:', error);
              // Hata durumunda kullanıcıyı bilgilendir ama sayfayı yenileme
              toast.error('Failed to refresh photos. Please refresh the page.');
            }
          }}
        />
      )}

      {/* Photo Expanded View Modal */}
      {expandedPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setExpandedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            {isExpandedPhotoLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            <img 
              src={expandedPhoto.public_url} 
              alt={expandedPhoto.file_name || "Expanded photo"}
              className={`max-w-full max-h-[90vh] object-contain transition-opacity duration-300 ${isExpandedPhotoLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={() => setIsExpandedPhotoLoading(false)}
            />
            <button 
              className="absolute top-4 right-4 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedPhoto(null);
                setIsExpandedPhotoLoading(false);
              }}
            >
              <X className="w-4 h-4" />
            </button>
            <button 
              className="absolute bottom-4 right-4 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPhoto(expandedPhoto, e);
              }}
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicEventPage;